/**
 * Which jobs this user has already read the notes on — the unread-note
 * badge's memory, shared by every app that shows a job card.
 *
 * <p>The shop's ask: when somebody writes a note on a job, everyone else
 * on that job must see it without opening the job. "Has notes" comes off
 * the feed (`lastNoteAt`, `lastNoteAuthorUserId`); "has notes I HAVE NOT
 * READ" is this file.
 *
 * <p>Markers live in `wo_note_reads` on the server (V0255): one row per
 * user and job, stamped forward when the job is opened. Each app
 * instantiates the store against its own read-marker routes through
 * {@link createNotesSeenStore} — management under `/admin/work-orders`,
 * the technician app under `/technical/work-orders` — while the badge
 * predicate {@link hasUnreadNote} is pure and shared as is. Hoisted out
 * of the management app on 2026-09-22, when the technician app got the
 * same badge; nothing about the behaviour changed with the move.
 *
 * <h3>How it behaves</h3>
 *
 * <p>The first read for a user fetches their markers once; until they
 * arrive the badge shows, which is the honest answer when we cannot tell
 * what has been read. Marking is optimistic: the in-memory marker clears
 * the badge at once and the write goes out in the background. A refused
 * write is forgotten on reload — the old per-device behaviour at worst,
 * never a badge that will not go away when clicked. A mark made while the
 * fetch is in flight is newer than anything the server knows and wins the
 * merge.
 *
 * <p>Anonymous (`userId` null, the session still resolving) has no markers
 * and marks nothing.
 */
import { useEffect, useSyncExternalStore } from 'react'

/** Job id → ISO time the user last read that job's notes. */
export type SeenMap = Readonly<Record<string, string>>

interface MarkerRow {
  readonly workOrderId: string
  readonly lastReadAt: string
}

/** The slice of an API client the store needs — any app's client fits. */
export interface NotesSeenApi {
  get<T>(path: string): Promise<T>
  put<T>(path: string): Promise<T>
}

export interface NotesSeenStoreOptions {
  readonly api: NotesSeenApi
  /**
   * The route prefix that owns `GET {basePath}/notes/read` (the caller's
   * markers) and `PUT {basePath}/{id}/notes/read` (mark one job read).
   */
  readonly basePath: string
}

export interface NotesSeenStore {
  /** The user is looking at this job's notes now. */
  markNotesSeen(userId: string | null, workOrderId: string): void
  /** The signed-in user's markers, job id → ISO time last read. */
  useNotesSeenAt(userId: string | null): SeenMap
  /**
   * Whether the markers have been asked for once (answered or refused).
   * Until then a badge must not render: it used to show while unknown and
   * vanish a second later when the markers said "read" — a badge that
   * disappears reads as a bug, a badge that appears late does not (user
   * report 2026-09-22). Nobody signed in is "not known yet" too: the
   * session resolving on page load must not flash a badge that the
   * user's own markers hide a moment later.
   */
  useNotesSeenReady(userId: string | null): boolean
  __resetForTests(): void
}

const EMPTY: SeenMap = {}

export function createNotesSeenStore({ api, basePath }: NotesSeenStoreOptions): NotesSeenStore {
  const listeners = new Set<() => void>()
  /** Per user: the union of what the server holds and what was marked here. */
  const cache = new Map<string, SeenMap>()
  /** Users whose markers were fetched (a failed fetch is retried on the next mount). */
  const loaded = new Set<string>()
  const loading = new Set<string>()
  /** Users whose fetch has come back at all — with markers or with an error. */
  const attempted = new Set<string>()

  function notify(): void {
    for (const fn of listeners) fn()
  }

  function load(userId: string): void {
    if (loaded.has(userId) || loading.has(userId)) return
    loading.add(userId)
    api
      .get<readonly MarkerRow[]>(`${basePath}/notes/read`)
      .then((rows) => {
        const fromServer: Record<string, string> = {}
        for (const r of rows) fromServer[r.workOrderId] = r.lastReadAt
        // Local marks first in time are later in the spread: a job opened
        // while this request was in flight is read, whatever the server says.
        cache.set(userId, { ...fromServer, ...(cache.get(userId) ?? EMPTY) })
        loaded.add(userId)
      })
      .catch(() => {
        // Cannot tell what was read — the badge shows. Not marked as loaded,
        // so the next mount tries again.
      })
      .finally(() => {
        loading.delete(userId)
        attempted.add(userId)
        notify()
      })
  }

  function markNotesSeen(userId: string | null, workOrderId: string): void {
    if (!userId || !workOrderId) return
    cache.set(userId, { ...(cache.get(userId) ?? EMPTY), [workOrderId]: new Date().toISOString() })
    notify()
    void api
      .put<void>(`${basePath}/${encodeURIComponent(workOrderId)}/notes/read`)
      .catch(() => {
        // The in-memory marker still clears the badge for this session.
      })
  }

  function subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  }

  function useNotesSeenAt(userId: string | null): SeenMap {
    useEffect(() => {
      if (userId) load(userId)
    }, [userId])
    return useSyncExternalStore(
      subscribe,
      () => (userId ? (cache.get(userId) ?? EMPTY) : EMPTY),
      () => EMPTY,
    )
  }

  function useNotesSeenReady(userId: string | null): boolean {
    useEffect(() => {
      if (userId) load(userId)
    }, [userId])
    return useSyncExternalStore(
      subscribe,
      () => (userId != null && attempted.has(userId)),
      () => false,
    )
  }

  function __resetForTests(): void {
    cache.clear()
    loaded.clear()
    loading.clear()
    attempted.clear()
    listeners.clear()
  }

  return { markNotesSeen, useNotesSeenAt, useNotesSeenReady, __resetForTests }
}

/**
 * Whether the job's newest note is one this reader has not read: written
 * by somebody else, and later than their marker (or there is no marker).
 */
export function hasUnreadNote(
  seenAt: SeenMap,
  workOrderId: string | null | undefined,
  lastNoteAt: Date | null | undefined,
  lastNoteAuthorUserId: string | null | undefined,
  meId: string | null,
): boolean {
  if (!workOrderId || !lastNoteAt) return false
  if (meId && lastNoteAuthorUserId === meId) return false
  const seen = seenAt[workOrderId]
  if (!seen) return true
  const seenMs = Date.parse(seen)
  return !Number.isFinite(seenMs) || seenMs < lastNoteAt.getTime()
}
